import Microgame from '../engine/Microgame.js';

export default class ParkGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Gare-toi !`, en: `Park It!`, es: `¡Aparca!` },
            controls: { fr: `Glisse ton doigt (ou clic) pour diriger la voiture`, en: `Swipe (or click) to steer the car`, es: `Desliza (o haz clic) para conducir el coche` },
            objective: { fr: `Mets la voiture sur la place de parking désignée`, en: `Put the car in the designated parking spot`, es: `Coloca el coche en la plaza de aparcamiento indicada` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>`,
            actions: [
                { type: 'wait', duration: 980 },
                { type: 'tap', x: 50, y: 72, duration: 140 },
                { type: 'wait', duration: 520 },
                { type: 'tap', x: 50, y: 56, duration: 140 },
                { type: 'wait', duration: 520 },
                { type: 'tap', x: 50, y: 40, duration: 140 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.car = null;
        this.spot = null;
        this.stopped = false;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;

        // Duration: getting tighter with speed
        const adjustedSpeed = 1 + (speedMult - 1) * (1.5 / 3);
        this.duration = 2000 * (speedMult / adjustedSpeed);

        // Directions: 0 = UP, 1 = DOWN, 2 = LEFT, 3 = RIGHT
        this.dir = this.isPlayback ? 0 : Math.floor(Math.random() * 4);

        // Percentage coordinates for the spot (center screen is 50,50)
        // We randomize the spot location to be somewhere in the middle half of the screen
        this.targetX = this.isPlayback ? 50 : 30 + Math.random() * 40; // 30% to 70%
        this.targetY = this.isPlayback ? 50 : 30 + Math.random() * 40; // 30% to 70%

        // Calculate starting position so that the car intersects the spot exactly at 85% of the game's duration
        this.progress = 0; // Goes from 0 at start, and crosses the target around 0.85s ratio
        this.winWindow = 0.07; // Very strict window to park perfectly
    }

    setup() {
        this.engine.ui.instruction.textContent = "PARK!";

        const isVertical = (this.dir === 0 || this.dir === 1);
        const carWidth = isVertical ? 60 : 100;
        const carHeight = isVertical ? 100 : 60;

        const spotWidth = carWidth + 14;
        const spotHeight = carHeight + 14;

        // Asphalt Base for the parking area
        const asphalt = document.createElement('div');
        asphalt.style.position = 'absolute';
        asphalt.style.background = 'rgba(0,0,0,0.3)';
        asphalt.style.borderRadius = '5px';
        if (isVertical) {
            // Stack horizontally side-by-side
            asphalt.style.width = `${spotWidth * 3 + 20}px`;
            asphalt.style.height = `${spotHeight + 20}px`;
        } else {
            // Stack vertically one above another
            asphalt.style.width = `${spotWidth + 20}px`;
            asphalt.style.height = `${spotHeight * 3 + 20}px`;
        }
        asphalt.style.top = `${this.targetY}%`;
        asphalt.style.left = `${this.targetX}%`;
        asphalt.style.transform = 'translate(-50%, -50%)';
        this.container.appendChild(asphalt);

        // Draw exactly 3 Parking spots (-1, 0, 1) and put cars in -1 and 1
        for (let i = -1; i <= 1; i++) {
            const space = document.createElement('div');
            space.style.position = 'absolute';
            space.style.width = `${spotWidth}px`;
            space.style.height = `${spotHeight}px`;

            if (isVertical) {
                // Stack horizontally (places aligned side-by-side)
                space.style.top = `${this.targetY}%`;
                space.style.left = `calc(${this.targetX}% + ${i * spotWidth}px)`;
                // Divider lines
                if (i !== 1) space.style.borderRight = `3px solid ${this.secondaryColor}`;
            } else {
                // Stack vertically (places aligned one above another)
                space.style.left = `${this.targetX}%`;
                space.style.top = `calc(${this.targetY}% + ${i * spotHeight}px)`;
                if (i !== 1) space.style.borderBottom = `3px solid ${this.secondaryColor}`;
            }
            space.style.transform = 'translate(-50%, -50%)';

            if (i === 0) {
                // This is the target spot
                this.spot = space;
                const glow = document.createElement('div');
                glow.style.width = '100%';
                glow.style.height = '100%';
                glow.style.background = this.primaryColor;
                glow.style.opacity = '0.35';
                this.spot.appendChild(glow);
            } else {
                // ALWAYS put a static car here to frame the spot
                this.createStaticCarInSpot(space, isVertical, carWidth, carHeight);
            }

            this.container.appendChild(space);
        }

        // Player Car
        this.car = document.createElement('div');
        this.car.style.position = 'absolute';
        this.car.style.width = `${carWidth}px`;
        this.car.style.height = `${carHeight}px`;
        this.car.style.background = this.primaryColor;
        this.car.style.borderRadius = '10px';
        this.car.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.car.style.zIndex = '10'; // Keep player car on top

        // Simple windshield relative to direction
        const win = document.createElement('div');
        win.style.position = 'absolute';
        win.style.background = 'rgba(0,0,0,0.3)';
        win.style.borderRadius = '4px';

        if (this.dir === 0) { // UP
            win.style.top = '10px'; win.style.left = '5px'; win.style.right = '5px'; win.style.height = '20px';
        } else if (this.dir === 1) { // DOWN
            win.style.bottom = '10px'; win.style.left = '5px'; win.style.right = '5px'; win.style.height = '20px';
        } else if (this.dir === 2) { // LEFT
            win.style.left = '10px'; win.style.top = '5px'; win.style.bottom = '5px'; win.style.width = '20px';
        } else if (this.dir === 3) { // RIGHT
            win.style.right = '10px'; win.style.top = '5px'; win.style.bottom = '5px'; win.style.width = '20px';
        }

        this.car.appendChild(win);
        this.container.appendChild(this.car);

        // Define Start and End coordinates based on direction so the car travels across the screen
        this.startX = this.targetX;
        this.startY = this.targetY;
        this.endX = this.targetX;
        this.endY = this.targetY;

        if (this.dir === 0) { // UP
            this.startY = 120; // Off bottom
            const ratio = (this.startY - this.targetY) / 0.85; // distance covered in 0.85 progress
            this.endY = this.startY - ratio; // position at progress 1.0
        } else if (this.dir === 1) { // DOWN
            this.startY = -20; // Off top
            const ratio = (this.targetY - this.startY) / 0.85;
            this.endY = this.startY + ratio;
        } else if (this.dir === 2) { // LEFT
            this.startX = 120; // Off right
            const ratio = (this.startX - this.targetX) / 0.85;
            this.endX = this.startX - ratio;
        } else if (this.dir === 3) { // RIGHT
            this.startX = -20; // Off left
            const ratio = (this.targetX - this.startX) / 0.85;
            this.endX = this.startX + ratio;
        }

        this.updateCarPosition();

        this.onDown = this.onDown.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
    }

    createStaticCarInSpot(container, isVertical, carWidth, carHeight) {
        const car = document.createElement('div');
        car.style.position = 'absolute';
        car.style.width = `${carWidth}px`;
        car.style.height = `${carHeight}px`;
        car.style.background = 'transparent';
        car.style.border = `2px solid ${this.secondaryColor}`;
        car.style.borderRadius = '10px';
        car.style.top = '50%';
        car.style.left = '50%';
        car.style.transform = 'translate(-50%, -50%)';
        car.style.opacity = '0.6';

        const win = document.createElement('div');
        win.style.position = 'absolute';
        win.style.background = 'rgba(0,0,0,0.3)';
        win.style.borderRadius = '4px';

        // Match rotation randomly
        if (isVertical) {
            if (Math.random() > 0.5) {
                win.style.top = '10px'; win.style.left = '5px'; win.style.right = '5px'; win.style.height = '20px';
            } else {
                win.style.bottom = '10px'; win.style.left = '5px'; win.style.right = '5px'; win.style.height = '20px';
            }
        } else {
            if (Math.random() > 0.5) {
                win.style.left = '10px'; win.style.top = '5px'; win.style.bottom = '5px'; win.style.width = '20px';
            } else {
                win.style.right = '10px'; win.style.top = '5px'; win.style.bottom = '5px'; win.style.width = '20px';
            }
        }

        car.appendChild(win);
        container.appendChild(car);
    }

    updateCarPosition() {
        const curX = this.startX + (this.endX - this.startX) * this.progress;
        const curY = this.startY + (this.endY - this.startY) * this.progress;
        this.car.style.left = `${curX}%`;
        this.car.style.top = `${curY}%`;
        this.car.style.transform = 'translate(-50%, -50%)';
    }

    update(dt) {
        if (!this.active || this.won || this.stopped) return;

        // CRUCIAL BUGFIX: calculate progress based strictly on the engine's exact running deadline
        // This ensures the car ALWAYS scales its visual speed down to hit 0.85 at exactly 85% of time remaining.
        this.progress += (dt / this.engine.maxTime);
        this.updateCarPosition();

        // Missed (drove past the end)
        if (this.progress >= 1.0) {
            this.lose();
            this.stopped = true;
            // Crash visual
            if (this.dir === 0 || this.dir === 1) {
                this.car.style.transform = 'translate(-50%, -50%) scaleY(0.8)';
            } else {
                this.car.style.transform = 'translate(-50%, -50%) scaleX(0.8)';
            }
            this.car.style.background = this.secondaryColor;
        }
    }

    onDown() {
        if (!this.active || this.won || this.stopped) return;
        this.stopped = true;

        // Check if progress is near 0.85 (the perfect spot)
        const dist = Math.abs(this.progress - 0.85);

        if (dist <= this.winWindow) {
            this.win();
            this.spot.style.borderColor = this.primaryColor;
            this.spot.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        } else {
            this.lose();
            this.spot.style.borderColor = this.secondaryColor;
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
    }
}
